import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactECharts from "echarts-for-react";

// ── Helpers (inlined from grid_v2 utils/tradeChart) ──────────────────────

const fmt = (ts) => {
  const d = new Date(ts * 1000);
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0") +
    " " +
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0")
  );
};

function calculatePrecision(candles) {
  if (!candles.length) return 2;
  const avg = candles.reduce((s, c) => s + c.close, 0) / candles.length;
  if (avg < 0.00001) return 8;
  if (avg < 0.001) return 6;
  if (avg < 1) return 4;
  if (avg < 100) return 3;
  return 2;
}

function buildOrderMarkers(candles, markers = []) {
  if (!candles.length || !markers.length) return [];
  const precision = calculatePrecision(candles);
  const lastTime = candles[candles.length - 1].time;
  return markers.map((m) => ({
    time: m.unixTime || lastTime,
    price: Number(m.price),
    type: m.type,
    label: `${m.label} @ ${Number(m.price).toFixed(precision)}`,
  }));
}

// ── Date/Time helpers ─────────────────────────────────────────────────────

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDateTime = (unixSeconds) => {
  const d = new Date(unixSeconds * 1000);
  const opts = { timeZone: userTimeZone };
  const local = new Date(d.toLocaleString("en-US", opts));
  const dd = String(local.getDate()).padStart(2, "0");
  const mo = String(local.getMonth() + 1).padStart(2, "0");
  const yyyy = local.getFullYear();
  const hh = String(local.getHours()).padStart(2, "0");
  const mm = String(local.getMinutes()).padStart(2, "0");
  return `${dd}/${mo}/${yyyy}, ${hh}:${mm}`;
};

const formatDateShort = (unixSeconds) => {
  const d = new Date(unixSeconds * 1000);
  const opts = { timeZone: userTimeZone };
  const local = new Date(d.toLocaleString("en-US", opts));
  const dd = String(local.getDate()).padStart(2, "0");
  const mo = String(local.getMonth() + 1).padStart(2, "0");
  const yy = String(local.getFullYear()).slice(-2);
  const hh = String(local.getHours()).padStart(2, "0");
  const mm = String(local.getMinutes()).padStart(2, "0");
  return `${dd}/${mo}/${yy} ${hh}:${mm}`;
};

// ── OKX candle fetching (public endpoint, no auth) ────────────────────────

const TF_MAP = { "15M": "15m", "1H": "1H", "4H": "4H", "1D": "1Dutc", "1W": "1W" };

const getTimeRange = (tf, limit) => {
  const minutes = { "15M": 15, "1H": 60, "4H": 240, "1D": 1440, "1W": 10080 }[tf] || 60;
  const now = Math.floor(Date.now() / 1000);
  return { from: now - limit * minutes * 60, to: now };
};

const fetchOKXCandles = async (instId, tf, endTimeMs = null, limit = 300) => {
  const bar = TF_MAP[tf] || "1H";
  const params = new URLSearchParams({ instId, bar, limit: String(Math.min(limit, 300)) });
  if (endTimeMs) params.append("after", String(endTimeMs));
  const endpoint = endTimeMs ? "history-candles" : "candles";
  try {
    const resp = await fetch(`https://www.okx.com/api/v5/market/${endpoint}?${params}`);
    const json = await resp.json();
    if (json.code !== "0") return [];
    return json.data
      .filter((r) => !endTimeMs || r[8] === "1") // skip forming candle on live fetch
      .map((r) => ({
        time: Math.floor(parseInt(r[0]) / 1000),
        open: parseFloat(r[1]),
        high: parseFloat(r[2]),
        low: parseFloat(r[3]),
        close: parseFloat(r[4]),
      }))
      .reverse(); // oldest-first
  } catch {
    return [];
  }
};

// ── Build activity data from trades + open orders ─────────────────────────

function buildActivityData(trades, openOrdersDetail) {
  const markers = (trades || []).flatMap((t) => {
    const out = [];
    if (t.entry_time && t.entry_price) {
      out.push({
        type: t.direction === "SHORT" ? "sell" : "buy",
        price: t.entry_price,
        label: `Entry ${t.direction || ""}`,
        unixTime: Math.floor(new Date(t.entry_time).getTime() / 1000),
      });
    }
    if (t.exit_time && t.exit_price) {
      const pnl = parseFloat(t.profit_loss || 0);
      out.push({
        type: t.direction === "SHORT" ? "buy" : "sell",
        price: t.exit_price,
        label: `Exit ${t.direction || ""} ${pnl >= 0 ? "+" : ""}${pnl.toFixed(4)} USDT`,
        unixTime: Math.floor(new Date(t.exit_time).getTime() / 1000),
      });
    }
    return out;
  });

  const grid_lines = (openOrdersDetail || [])
    .map((o) => ({ price: Number(o.entry_price), label: o.side || "", type: o.side?.toLowerCase() || "" }))
    .filter((g) => !isNaN(g.price));

  return { markers, grid_lines };
}

// ── X-Axis Overlay (identical to grid_v2) ────────────────────────────────

const XAXIS_HEIGHT = 28;

function XAxisOverlay({ chartRef, candles, zoomState }) {
  const [labels, setLabels] = useState([]);
  const rafRef = useRef(null);

  const computeLabels = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const chart = chartRef.current?.getEchartsInstance();
      if (!chart || !candles.length) return;
      const opt = chart.getOption();
      const dz = opt?.dataZoom?.[0];
      if (!dz) return;
      const startPct = (dz.start ?? zoomState.start) / 100;
      const endPct = (dz.end ?? zoomState.end) / 100;
      const total = candles.length;
      const startIdx = Math.max(0, Math.floor(startPct * total));
      const endIdx = Math.min(total - 1, Math.ceil(endPct * total));
      const visible = Math.max(1, endIdx - startIdx);
      const pxStart = chart.convertToPixel({ gridIndex: 0 }, [startIdx, 0]);
      const pxEnd = chart.convertToPixel({ gridIndex: 0 }, [endIdx, 0]);
      if (!pxStart || !pxEnd) return;
      const totalPxWidth = Math.abs(pxEnd[0] - pxStart[0]);
      const minLabelPx = 72;
      const maxLabels = Math.max(1, Math.floor(totalPxWidth / minLabelPx));
      const step = Math.ceil(visible / maxLabels);
      const result = [];
      let lastX = -Infinity;
      for (let i = startIdx; i <= endIdx; i++) {
        if ((i - startIdx) % step !== 0) continue;
        try {
          const px = chart.convertToPixel({ gridIndex: 0 }, [i, 0]);
          if (!px) continue;
          const x = px[0];
          if (x - lastX < minLabelPx) continue;
          lastX = x;
          result.push({ x, label: formatDateShort(candles[i].time) });
        } catch (_) {}
      }
      setLabels(result);
    });
  }, [chartRef, candles, zoomState]);

  useEffect(() => {
    computeLabels();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [computeLabels]);

  useEffect(() => {
    const onResize = () => computeLabels();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [computeLabels]);

  useEffect(() => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;
    chart.on("finished", computeLabels);
    return () => chart.off("finished", computeLabels);
  }, [chartRef, computeLabels]);

  return (
    <div style={{ position: "relative", height: XAXIS_HEIGHT, background: "#FFFFFF", borderTop: "1px solid #E0E3EB", overflow: "hidden" }}>
      {labels.map((l, i) => (
        <span key={i} style={{ position: "absolute", left: l.x, top: "50%", transform: "translate(-50%, -50%)", fontSize: 10, color: "#787B86", whiteSpace: "nowrap", pointerEvents: "none" }}>
          {l.label}
        </span>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────

export default function BotActivityChart({ bot, trades, detail, height = 450, defaultPeriod = "1H" }) {
  const [selectedTimeframe, setSelectedTimeframe] = useState(defaultPeriod);
  const [candles, setCandles] = useState([]);
  const [zoomState, setZoomState] = useState({ start: 50, end: 100 });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [livePrice, setLivePrice] = useState(null);

  const wsRef = useRef(null);
  const chartRef = useRef(null);
  const loadingRef = useRef(false);
  const wsReadyRef = useRef(false);
  const dataZoomTimeoutRef = useRef(null);

  const instId = useMemo(() => {
    const s = (bot.symbol || "").toUpperCase();
    for (const q of ["USDT", "USDC", "BTC", "ETH"]) {
      if (s.endsWith(q)) return `${s.slice(0, -q.length)}-${q}`;
    }
    return s;
  }, [bot.symbol]);

  const activityData = useMemo(
    () => buildActivityData(trades, detail?.open_orders_detail),
    [trades, detail]
  );

  // Load initial candles
  useEffect(() => {
    let active = true;
    setCandles([]);
    setHasMoreData(true);
    loadingRef.current = false;
    setIsLoadingMore(false);
    setLivePrice(null);
    wsReadyRef.current = false;

    fetchOKXCandles(instId, selectedTimeframe, null, 200).then((data) => {
      if (!active) return;
      if (data.length > 0) {
        setCandles(data);
        setLivePrice(data[data.length - 1].close);
        wsReadyRef.current = true;
        setTimeout(() => {
          const targetCandles = 50;
          const total = data.length;
          setZoomState({ start: Math.max(0, ((total - targetCandles) / total) * 100), end: 100 });
        }, 50);
      } else {
        wsReadyRef.current = true;
      }
    });

    return () => { active = false; };
  }, [instId, selectedTimeframe]);

  // Load more historical candles when scrolling left
  const loadMoreCandles = async () => {
    if (loadingRef.current || !hasMoreData || candles.length === 0) return;
    loadingRef.current = true;
    setIsLoadingMore(true);

    const chart = chartRef.current?.getEchartsInstance();
    let savedStartIndex = null;
    let savedEndIndex = null;
    if (chart) {
      try {
        const option = chart.getOption();
        if (option.dataZoom?.[0]) {
          savedStartIndex = Math.floor((candles.length * (option.dataZoom[0].start || 0)) / 100);
          savedEndIndex = Math.floor((candles.length * (option.dataZoom[0].end || 100)) / 100);
        }
      } catch (_) {}
    }

    try {
      const oldestMs = candles[0].time * 1000;
      const oldCandleCount = candles.length;
      const moreData = await fetchOKXCandles(instId, selectedTimeframe, oldestMs, 200);
      const existingTimes = new Set(candles.map((c) => c.time));
      const newCandles = moreData.filter((c) => !existingTimes.has(c.time));

      if (newCandles.length === 0) {
        setHasMoreData(false);
        loadingRef.current = false;
        setIsLoadingMore(false);
        return;
      }
      if (newCandles.length < 20) setHasMoreData(false);

      const actualNewCount = newCandles.length;
      setCandles((prev) => [...newCandles, ...prev].sort((a, b) => a.time - b.time));

      setTimeout(() => {
        const chart = chartRef.current?.getEchartsInstance();
        if (chart && savedStartIndex !== null && actualNewCount > 0) {
          const newTotal = oldCandleCount + actualNewCount;
          const newStart = Math.max(0, ((savedStartIndex + actualNewCount) / newTotal) * 100);
          const newEnd = Math.min(100, ((savedEndIndex + actualNewCount) / newTotal) * 100);
          chart.setOption({ dataZoom: [{ start: newStart, end: newEnd }] }, { lazyUpdate: true });
          setZoomState({ start: newStart, end: newEnd });
        }
        loadingRef.current = false;
        setIsLoadingMore(false);
      }, 150);
    } catch (_) {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  };

  // OKX WebSocket for live price
  useEffect(() => {
    if (!instId) return;
    if (wsRef.current) wsRef.current.close();

    const ws = new WebSocket("wss://ws.okx.com:8443/ws/v5/public");
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ op: "subscribe", args: [{ channel: "tickers", instId }] }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const ticker = data?.data?.[0];
        if (!ticker?.last || !wsReadyRef.current) return;
        const liveClose = parseFloat(ticker.last);
        if (isNaN(liveClose)) return;
        setLivePrice(liveClose);
        setCandles((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = {
            ...last,
            close: liveClose,
            high: Math.max(last.high, liveClose),
            low: Math.min(last.low, liveClose),
          };
          return updated;
        });
      } catch (_) {}
    };

    ws.onerror = () => {};
    ws.onclose = () => {};
    return () => ws.close();
  }, [instId, selectedTimeframe]);

  // Build markers
  const orderMarkers = useMemo(() => {
    if (!candles.length) return [];
    return buildOrderMarkers(candles, activityData?.markers || []);
  }, [candles, activityData]);

  // Grid lines from open orders
  const gridLines = useMemo(() => {
    if (!activityData?.grid_lines || !Array.isArray(activityData.grid_lines)) return [];
    return activityData.grid_lines.map((line) => ({ ...line, price: Number(line.price) })).filter((l) => !isNaN(l.price));
  }, [activityData]);

  const calculateBarWidth = () => {
    const visibleRange = zoomState.end - zoomState.start;
    const visibleCandles = Math.floor((candles.length * visibleRange) / 100);
    if (visibleCandles > 200) return "40%";
    if (visibleCandles > 100) return "50%";
    if (visibleCandles > 50) return "60%";
    if (visibleCandles > 30) return "70%";
    return "80%";
  };

  function getOption() {
    if (!candles || candles.length === 0) {
      return {
        backgroundColor: "#FFFFFF",
        grid: { left: "1%", right: "3%", top: "5%", bottom: 0, containLabel: false },
        xAxis: { type: "category", data: [] },
        yAxis: { type: "value" },
        series: [],
      };
    }

    const precision = calculatePrecision(candles);
    const lastCandle = candles[candles.length - 1];
    const higherTimeframes = ["4H", "1D", "1W"];
    const isHigherTF = higherTimeframes.includes(selectedTimeframe);
    const displayPrice = livePrice != null
      ? isHigherTF ? Math.min(lastCandle.high, Math.max(lastCandle.low, livePrice)) : livePrice
      : null;
    const priceIsUp = lastCandle.close >= lastCandle.open;

    return {
      backgroundColor: "#FFFFFF",
      animation: false,
      grid: { left: "1%", right: "3%", top: "5%", bottom: 0, containLabel: false },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: function (params) {
          if (!params || params.length === 0) return "";
          const hasMarkerHover = params.some((p) => p.seriesType === "scatter" && p.componentSubType === "scatter");
          if (hasMarkerHover) return "";
          const candleParam = params.find((p) => p.seriesType === "candlestick");
          if (!candleParam || candleParam.dataIndex === undefined) return "";
          const candle = candles[candleParam.dataIndex];
          if (!candle) return "";
          return `<div style="padding:4px;"><div style="font-weight:bold;">${formatDateTime(candle.time)}</div><div>O: ${candle.open.toFixed(precision)}</div><div>H: ${candle.high.toFixed(precision)}</div><div>L: ${candle.low.toFixed(precision)}</div><div>C: ${candle.close.toFixed(precision)}</div></div>`;
        },
      },
      xAxis: {
        type: "category",
        data: candles.map((c) => fmt(c.time)),
        boundaryGap: true,
        axisLine: { lineStyle: { color: "#E0E3EB" }, onZero: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        scale: true,
        position: "right",
        splitLine: { show: true },
        min: (value) => {
          const range = value.max - value.min;
          const buffer = range < 0.00001 ? value.min * 0.01 : range * 0.2;
          return value.min - buffer;
        },
        max: (value) => {
          const range = value.max - value.min;
          const buffer = range < 0.00001 ? value.max * 0.01 : range * 0.2;
          return value.max + buffer;
        },
        axisLabel: { formatter: (val) => val.toFixed(precision), color: "#787B86" },
      },
      dataZoom: [
        { type: "inside", xAxisIndex: [0], start: zoomState.start, end: zoomState.end, filterMode: "filter", zoomOnMouseWheel: true, moveOnMouseMove: true, minValueSpan: 10 },
        { type: "inside", yAxisIndex: [0], filterMode: "none", zoomOnMouseWheel: true, moveOnMouseMove: true },
      ],
      series: [
        {
          type: "candlestick",
          data: candles.map((c) => [c.open, c.close, c.low, c.high]),
          itemStyle: {
            color: "#26A69A",
            color0: "#EF5350",
            borderColor: "#26A69A",
            borderColor0: "#EF5350",
            borderWidth: 1,
          },
          barWidth: calculateBarWidth(),
          barMaxWidth: "90%",
          barMinWidth: 2,
        },

        ...(candles.length > 0 && gridLines.length > 0 ? [{
          type: "line",
          name: "GRID_LINES",
          silent: true,
          data: [],
          markLine: {
            symbol: "none",
            precision: precision,
            clip: false,
            data: gridLines.map((line) => ({
              yAxis: Number(line.price),
              label: {
                show: true,
                position: "end",
                formatter: (params) => `${params.value.toFixed(precision)}`,
                backgroundColor: "rgba(255, 255, 255, 1)",
                color: "#2962FF",
                padding: [3, 5],
                borderRadius: 4,
                fontSize: 10,
                fontWeight: "bold",
                borderWidth: 1,
                borderColor: "#2962FF",
              },
              lineStyle: { width: 1, color: "#2962FF", type: "dashed" },
            })),
          },
        }] : []),

        ...(displayPrice != null && candles.length > 0 ? [{
          type: "line",
          name: "LIVE PRICE",
          markLine: {
            silent: true,
            symbol: "none",
            data: [{ yAxis: displayPrice }],
            label: {
              show: true,
              formatter: () => displayPrice != null ? displayPrice.toFixed(precision) : "",
              position: "end",
              backgroundColor: priceIsUp ? "#26A69A" : "#EF5350",
              color: "#fff",
              padding: [4, 6],
              borderRadius: 4,
              fontSize: 12,
              fontWeight: "bold",
            },
            lineStyle: { width: 1.2, color: priceIsUp ? "#26A69A" : "#EF5350", type: "solid" },
          },
          data: [],
        }] : []),

        {
          type: "scatter",
          name: "Trade Orders",
          z: 10,
          symbolSize: 24,
          data: (() => {
            if (!orderMarkers.length) return [];
            const stackMap = {};
            return orderMarkers.map((m) => {
              const markerTime = m.time;
              let matchIdx = candles.findIndex((c, i) => {
                const nextCandleTime = candles[i + 1] ? candles[i + 1].time : Infinity;
                return markerTime >= c.time && markerTime < nextCandleTime;
              });
              if (matchIdx === -1) matchIdx = candles.findIndex((c) => c.time === markerTime);
              if (matchIdx === -1) return null;
              const candle = candles[matchIdx];
              if (!candle) return null;
              const stackKey = `${matchIdx}_${m.type}`;
              const stackCount = stackMap[stackKey] || 0;
              stackMap[stackKey] = stackCount + 1;
              const priceGap = candle.close * 0.0075;
              const safetyBuffer = candle.close * 0.02;
              const totalOffset = safetyBuffer + priceGap * stackCount * 4;
              const yPos = m.type === "buy" ? candle.low - totalOffset : candle.high + totalOffset;
              return {
                value: [fmt(candle.time), yPos],
                coord: [fmt(candle.time), yPos],
                markerType: m.type,
                markerData: m,
              };
            }).filter(Boolean);
          })(),

          symbol: function (value, params) {
            if (!params?.data?.markerType) return "circle";
            const imagePath = params.data.markerType === "buy"
              ? "/images/marker/buy_marker.png"
              : "/images/marker/sell_marker.png";
            return `image://${imagePath}`;
          },

          tooltip: {
            trigger: "item",
            formatter: (params) => {
              if (!params?.data?.markerData) return "";
              const m = params.data.markerData;
              const typeColor = m.type === "buy" ? "#26A69A" : "#EF5350";
              return `<div style="padding: 8px; border-left: 4px solid ${typeColor}; background: #fff;"><div style="color: ${typeColor}; font-weight: bold;">${m.type.toUpperCase()} ORDER</div><div style="color: #131722;">Price: ${Number(m.price).toFixed(calculatePrecision(candles))}</div><div style="font-size: 11px; color: #787B86;">${formatDateTime(m.time)}</div>${m.label ? `<div style="font-size:12px; margin-top:4px;">${m.label}</div>` : ""}</div>`;
            },
          },
        },
      ],
    };
  }

  const timeframes = ["15M", "1H", "4H", "1D", "1W"];
  const chartHeight = height - XAXIS_HEIGHT;

  return (
    <div style={{ backgroundColor: "#FFFFFF", position: "relative" }}>
      {isLoadingMore && (
        <div style={{ position: "absolute", top: "20px", left: "20px", background: "#2D224E", color: "white", padding: "8px 16px", borderRadius: "4px", fontSize: "12px", fontWeight: "bold", zIndex: 1000 }}>
          Loading historical data...
        </div>
      )}

      <ReactECharts
        ref={chartRef}
        option={getOption()}
        style={{ height: chartHeight, width: "100%" }}
        opts={{ renderer: "canvas" }}
        notMerge={false}
        lazyUpdate={true}
        onEvents={{
          dataZoom: () => {
            if (dataZoomTimeoutRef.current) clearTimeout(dataZoomTimeoutRef.current);
            dataZoomTimeoutRef.current = setTimeout(() => {
              const chart = chartRef.current?.getEchartsInstance();
              if (chart) {
                try {
                  const option = chart.getOption();
                  if (option.dataZoom?.[0]) {
                    const newStart = option.dataZoom[0].start || 0;
                    const newEnd = option.dataZoom[0].end || 100;
                    setZoomState({ start: newStart, end: newEnd });
                    if (newStart < 15 && !loadingRef.current && hasMoreData && candles.length > 0) {
                      loadMoreCandles();
                    }
                  }
                } catch (_) {}
              }
            }, 100);
          },
        }}
      />

      <XAxisOverlay chartRef={chartRef} candles={candles} zoomState={zoomState} />

      <div className="flex items-center gap-2 mt-3 pb-2">
        {timeframes.map((tf) => (
          <button
            key={tf}
            onClick={() => setSelectedTimeframe(tf)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              tf === selectedTimeframe
                ? "bg-[#F0F3FA] text-[#131722]"
                : "bg-transparent text-[#131722] hover:bg-[#F0F3FA]"
            }`}
          >
            {tf}
          </button>
        ))}
      </div>
    </div>
  );
}
